<wcg-profile-card
  [size]="cardSize()"
  data-testid="contact-wrapper__profile-card"
>
  @let backgroundColor = "#dde1e4";

  <ng-container wcgProfileCardHeader>
    <h2
      class="profile-contact__title flex flex-row items-center gap-4"
      data-testid="contact-wrapper__title"
    >
      <mat-icon
        fontSet="material-icons-outlined"
        class="!text-blue-11"
        data-testid="contact-wrapper__header-icon"
      >
        {{ icon() }}
      </mat-icon>
      {{ title() }}
    </h2>
  </ng-container>

  @if (canEdit() || !isInternal()) {
    <ng-container wcgProfileCardHeaderButton>
      <wcg-button
        color="tertiary"
        [isDisabled]="$setAddButton()"
        (click)="handleAddNewContact()"
        data-testid="contact-wrapper__add-button"
      >
        <mat-icon data-testid="contact-wrapper__add-icon">add</mat-icon>
        Add
      </wcg-button>
    </ng-container>
  }

  <ng-container wcgProfileCardContent>
    <div class="w-full flex flex-col gap-2">
      @if (!loading()) {
        @if (contactInfoSignalForm.assignedContacts().value().length > 0) {
          @for (
            contact of contactInfoSignalForm.assignedContacts;
            track $index;
            let isLast = $last
          ) {
            @let formField = contactInfoSignalForm.assignedContacts[$index];

            <div
              class="w-full p-[24px] flex flex-col gap-4"
              [ngClass]="{
                'profile-contact__border-bottom': !isLast || $setAddButton(),
              }"
              [attr.data-testid]="`contact-wrapper__assigned-contact-${$index}`"
            >
              <wcg-inline-editor
                class="flex gap-8"
                [turnoffInlineEdit]="false"
                [defaultView]="
                  $currentEditableIndex() === $index ? 'Edit' : 'View'
                "
                [attr.data-testid]="`contact-wrapper__inline-editor-${$index}`"
              >
                <ng-template wcgInlineViewMode>
                  <div
                    class="w-full flex flex-row justify-between items-center"
                    [attr.data-testid]="
                      `contact-wrapper__contact-view-${$index}`
                    "
                  >
                    @let _contact =
                      contactInfoSignalForm.assignedContacts[$index];

                    <span
                      class="profile-contact__info"
                      [attr.data-testid]="
                        `contact-wrapper__contact-info-{$index}`
                      "
                    >
                      @if (contactType() === "phone") {
                        {{
                          _contact.contact().value() | phoneFormat:true:_contact.countryCode().value().label
                        }}
                      } @else {
                        {{ _contact.contact().value() }}
                      }
                      @if (formField.isPrimary().value()) {
                        <strong
                          [attr.data-testid]="
                            `contact-wrapper__primary-label-{$index}`
                          "
                        >
                          (Primary)
                        </strong>
                      }
                    </span>

                    @if (
                      !(
                        contactType() === "email" &&
                        formField.isPrimary().value()
                      )
                    ) {
                      <span
                        class="profile-contact__actions"
                        [attr.data-testid]="
                          `contact-wrapper__actions-${$index}`
                        "
                      >
                        <ng-container
                          [ngTemplateOutlet]="kebabMenu"
                          [ngTemplateOutletContext]="{
                            props: { formField: formField, index: $index },
                          }"
                        />
                      </span>
                    }
                  </div>
                </ng-template>

                <ng-template wcgInlineEditMode>
                  @let _contactType = contactType();
                  <div
                    class="w-full flex flex-row gap-4 items-start"
                    [ngClass]="{
                      '!max-w-[65%] min-h-[20px]': _contactType === 'phone',
                      '!max-w-[85%]': _contactType === 'email',
                    }"
                    [attr.data-testid]="
                      `contact-wrapper__contact-inline-edit-${$index}`
                    "
                  >
                    @if (contactType() === "phone") {
                      <ng-container
                        [ngTemplateOutlet]="phoneNumberTemplate"
                        [ngTemplateOutletContext]="{
                          props: {
                            formField: formField,
                            isLast: isLast,
                            errorMessagePositions: { top: '70%', left: '4%' },
                          },
                        }"
                      />
                    } @else {
                      <ng-container
                        [ngTemplateOutlet]="emailAddressTemplate"
                        [ngTemplateOutletContext]="{
                          props: { formField: formField, isLast: isLast },
                        }"
                      />
                    }

                    @let phoneDisabled =
                      contactType() === "phone" &&
                      (!contactInfoSignalForm.assignedContacts[$index]
                        .countryCode()
                        .valid() ||
                        !contactInfoSignalForm.assignedContacts[$index]
                          .contact()
                          .valid());

                    @let emailDisabled =
                      contactType() === "email" &&
                      !contactInfoSignalForm.assignedContacts[$index]
                        .contact()
                        .valid();

                    <ng-container
                      [ngTemplateOutlet]="actionButtonRef"
                      [ngTemplateOutletContext]="{
                        props: {
                          index: $index,
                          isDisabled: phoneDisabled || emailDisabled,
                          isAddContact: false,
                        },
                      }"
                    />
                  </div>
                </ng-template>
              </wcg-inline-editor>

              @if (
                contactType() === "phone" && $currentEditableIndex() === $index
              ) {
                <div class="w-full relative right-[10px]">
                  <mat-checkbox
                    [formField]="formField.isPrimary"
                    color="primary"
                    [attr.data-testid]="
                      `contact-wrapper__primary-checkbox-${$index}`
                    "
                  >
                    Set as primary
                  </mat-checkbox>
                </div>
              }
            </div>
          }
        }
      } @else {
        @if (!$setAddButton()) {
          <div data-testid="contact-wrapper__loading-container">
            <ngx-skeleton-loader
              count="3"
              appearance="line"
              [theme]="{
                'border-radius': '5px',
                height: '20px',
                width: '95%',
                'background-color': backgroundColor,
                'margin-bottom': '10px',
                'margin-top': '10px',
                marginRight: '8px',
                marginLeft: '12px',
              }"
              data-testid="contact-wrapper__skeleton-loader"
            />
          </div>
        }
      }

      @if ($setAddButton()) {
        <div
          class="w-full flex flex-col gap-4 p-[24px]"
          data-testid="contact-wrapper__new-contact-form"
        >
          <div
            class="w-[90%] flex flex-row gap-4 items-start"
            [ngClass]="{
              '!max-w-[65%] m-h-[20px]': contactType() === 'phone',
              '!max-w-[85%]': contactType() === 'email',
            }"
          >
            @if (contactType() === "phone") {
              <ng-container
                [ngTemplateOutlet]="phoneNumberTemplate"
                [ngTemplateOutletContext]="{
                  props: {
                    formField: contactInfoSignalForm.contactForm,
                    isLast: true,
                    errorMessagePositions: { top: '70%', left: '4%' },
                  },
                }"
              />
            } @else {
              <ng-container
                [ngTemplateOutlet]="emailAddressTemplate"
                [ngTemplateOutletContext]="{
                  props: {
                    formField: contactInfoSignalForm.contactForm,
                    isLast: true,
                    errorMessagePositions: { top: '41%', left: '4%' },
                  },
                }"
              />
            }

            @let phoneDisabled =
              contactType() === "phone" &&
              (!contactInfoSignalForm.contactForm.countryCode().valid() ||
                !contactInfoSignalForm.contactForm.contact().valid());

            @let emailDisabled =
              contactType() === "email" &&
              !contactInfoSignalForm.contactForm.contact().valid();

            <ng-container
              [ngTemplateOutlet]="actionButtonRef"
              [ngTemplateOutletContext]="{
                props: {
                  isDisabled: phoneDisabled || emailDisabled,
                  isAddContact: true,
                  index: -1,
                  contactInfo: contactInfoSignalForm.contactForm().value(),
                },
              }"
            />
          </div>

          @if (contactType() === "phone") {
            <div class="w-full mt-[1rem] relative right-[10px]">
              <mat-checkbox
                [formField]="contactInfoSignalForm.contactForm.isPrimary"
                color="primary"
                data-testid="contact-wrapper__primary-checkbox-new"
              >
                Set as primary
              </mat-checkbox>
            </div>
          }
        </div>
      }
    </div>
  </ng-container>
</wcg-profile-card>

<ng-template
  #phoneNumberTemplate
  [typeValidator]="contactNumberTemplate"
  let-props="props"
>
  <div class="block w-full gap-4 p-0 m-0">
    <div class="w-full flex flex-row gap-8 items-center">
      <mat-form-field
        class="w-[55%] wrapper-select"
        appearance="outline"
        data-testid="contact-wrapper__country-code"
      >
        <mat-select
          [formField]="props.formField.countryCode"
          [compareWith]="compareObjectsFn"
        >
          @for (countryCode of countryCodes(); track countryCode.id) {
            <mat-option [value]="countryCode">{{
              countryCode.label
            }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <mat-form-field
        class="w-[45%] wrapper-input"
        appearance="outline"
        data-testid="contact-wrapper__phone-input"
      >
        <input
          matInput
          placeholder="Enter new phone number #"
          [formField]="props.formField.contact"
        />
      </mat-form-field>
    </div>
    <div>
      @if (props.formField.countryCode().invalid()) {
        <span class="w-full relative top-[13%] left-0">
          @for (
            error of props.formField.countryCode().errors();
            track error.kind
          ) {
            <mat-error>{{ error.message }}</mat-error>
          }
        </span>
      }
      @if (
        contactType() === "phone" &&
        props.formField.contact().invalid() &&
        props.formField.contact().touched()
      ) {
        @for (error of props.formField.contact().errors(); track error.kind) {
          <mat-error>{{ error.message }}</mat-error>
        }
      }
    </div>
  </div>
</ng-template>

<ng-template
  #emailAddressTemplate
  [typeValidator]="emailTemplate"
  let-props="props"
>
  <div class="w-full flex flex-col gap-4">
    <mat-form-field
      class="w-full wrapper-input"
      appearance="outline"
      data-testid="contact-wrapper__email-input"
    >
      <input
        matInput
        placeholder="Enter new email address"
        [formField]="props.formField.contact"
      />
    </mat-form-field>
    @if (
      props.formField.contact().invalid() && props.formField.contact().touched()
    ) {
      @for (error of props.formField.contact().errors(); track error.kind) {
        <mat-error>{{ error.message }}</mat-error>
      }
    }
  </div>
</ng-template>

<ng-template #kebabMenu [typeValidator]="KebabMenuTemplate" let-props="props">
  <mat-icon
    class="more_options"
    [matMenuTriggerFor]="menu"
    [attr.data-testid]="`contact-wrapper__kebab-menu-${props.index}`"
    >more_horiz</mat-icon
  >
  <mat-menu #menu="matMenu">
    @let currentEditableIndex = $currentEditableIndex();
    @let currentContactId =
      currentEditableIndex
        ? contactInfoSignalForm.assignedContacts[currentEditableIndex]
            .id()
            .value()
        : null;
    <button
      mat-menu-item
      [disabled]="!canEdit() || $setAddButton()"
      (click)="
        handleEditContact(
          $currentEditableIndex(),
          props.index,
          currentContactId
        )
      "
      [attr.data-testid]="`contact-wrapper__edit-button-${props.index}`"
    >
      Edit
    </button>
    <button
      mat-menu-item
      [disabled]="!canEdit() || $setAddButton()"
      (click)="deleteContact(props.formField().value(), props.index)"
      [attr.data-testid]="`contact-wrapper__delete-button-${props.index}`"
    >
      Delete
    </button>
  </mat-menu>
</ng-template>

<ng-template
  #actionButtonRef
  [typeValidator]="actionButtonTemplate"
  let-props="props"
>
  <div
    class="w-[20%] flex flex-row gap-4 pt-[0.75rem]"
    [ngClass]="{ '!w-[25%]': contactType() === 'phone' }"
    [attr.data-testid]="`contact-wrapper__action-buttons-${props.index}`"
  >
    <wcg-button
      color="tertiary"
      (click)="
        props.isAddContact
          ? handleNewContactCancel()
          : handleEditContactCancel()
      "
      [attr.data-testid]="`contact-wrapper__cancel-button-${props.index}`"
    >
      Cancel
    </wcg-button>

    <wcg-button
      color="primary"
      [isDisabled]="props.isDisabled"
      (click)="
        props.isAddContact
          ? addNewContact(props.contactInfo)
          : saveContactInfo(props.index)
      "
      [attr.data-testid]="`contact-wrapper__save-button-${props.index}`"
    >
      Save
    </wcg-button>
  </div>
</ng-template>
